import { type PropsWithChildren, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { SearchParams } from '../../../api/constants';
import { getClientUserSession } from '../../../api/portalServices';
import type { UserInfo } from '../../../api/types';
import { type ErrorCode, SESSION_ERROR_CODES } from '../../../errorHandler/constants';
import { useErrorContext } from '../../../errorHandler/context/ErrorContext';
import { initializePendo } from '../../../utils/pendo';
import { initTracerPortal } from '../../../utils/tracer';
import { getLoginUrl, getPortalUrl } from '../../../utils/utils';

export type SessionState = {
  tenantId?: string;
  tenantType?: string;
  workflowId?: string;
  userInfo?: UserInfo;
};

export const SessionContext = createContext<(SessionState & { clearSession: () => void }) | null>(null);

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionContext');
  }
  return context;
};

export const Session = ({ children }: PropsWithChildren) => {
  const [session, setSession] = useState<SessionState>();
  const [loading, setLoading] = useState(true);
  const [workflowId, setWorkflowId] = useState<string | undefined>(undefined);
  const [searchParams, setSearchParams] = useSearchParams();
  const [redirectToLookup, setRedirectToLookup] = useState(false);

  // Save the error on notification & Handle 401 errors and clearSession
  const { error, clearErrorNotifications } = useErrorContext();

  useEffect(() => {
    const path = window.location.pathname;
    let stateParam: string | null = null;
    /* During the EntraId configuration, we redirect to Microsoft for authentication.
    After a successful authentication, on redirect to portal we receive the tenantId in a query param named - "state".
    We have to read this and add a tenantId query param for all portal flows to work well,
    especially primary/test tenant scenarios. */
    if (path === '/entraid/auth-success') {
      stateParam = searchParams.get(SearchParams.STATE);
      if (stateParam) {
        searchParams.set(SearchParams.TENANT_ID, stateParam);
        setSearchParams(searchParams);
      }
    }
    if (!session) {
      const wid = uuidv4();
      initTracerPortal(wid); //TODO revisit this - causing issues within react query
      const checkUserSession = async (tenantId: string) => {
        // call getClientUserSession to check if the user is logged in
        return getClientUserSession('', tenantId);
      };
      checkUserSession(stateParam || '')
        .then((response) => {
          if (response) {
            initializePendo(response.tenantId, response.userInfo);
            setSession({
              tenantId: response.tenantId,
              tenantType: response.tenantType,
              userInfo: response.userInfo,
              workflowId: wid,
            });
            setLoading(false);
          }
        })
        .catch((e) => {
          console.log('Error fetching user session:', e);
          setLoading(false);
        })
        .finally(() => {
          setWorkflowId(wid);
        });
    }
  }, [session, searchParams, setSearchParams]);

  const clearSession = useCallback(() => {
    if (searchParams?.get(SearchParams.TENANT_ID)) {
      searchParams.delete(SearchParams.TENANT_ID);
      setSearchParams(searchParams);
    }
    setSession(undefined);
    // Once session is cleared, redirect to portal url without any tenant info
    setRedirectToLookup(true);
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (SESSION_ERROR_CODES.includes(error?.code as ErrorCode)) {
      console.debug('Session clearSession.');
      clearErrorNotifications();
      clearSession();
    }
  }, [error, clearErrorNotifications, clearSession]);

  const contextValue = useMemo(
    () => ({
      ...session,
      clearSession,
    }),
    [session, clearSession],
  );

  if (loading) {
    return <></>;
  }
  if (!session || !session.tenantId) {
    console.debug('Session not found, redirecting to login page.', redirectToLookup);
    window.location.href = redirectToLookup ? getPortalUrl() : getLoginUrl(workflowId || '');
    return <></>;
  }

  return <SessionContext.Provider value={contextValue}>{children}</SessionContext.Provider>;
};
